import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationHelpers } from '../components/NotificationSystem';
import { QuotationRequest, Invoice } from '../types';
import { BillData, PaymentRecord } from '../types/billing';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import EnhancedLoadingSpinner from '../components/EnhancedLoadingSpinner';
import PaymentModal from '../components/PaymentModal';
import ClientMessaging from '../components/ClientMessaging';

interface ProjectStatus {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed';
  startDate: string;
  endDate?: string;
}

const ClientDashboard: React.FC = () => {
  const { user, userProfile, logout } = useAuth();
  const { showError, showSuccess } = useNotificationHelpers();
  const [activeTab, setActiveTab] = useState<'overview' | 'quotes' | 'bills' | 'projects' | 'profile' | 'messages'>('overview');
  const [quotes, setQuotes] = useState<QuotationRequest[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bills, setBills] = useState<BillData[]>([]);
  const [projects, setProjects] = useState<ProjectStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState<BillData | null>(null);
  const [showMessaging, setShowMessaging] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [userProfile]);

  const loadClientData = async () => {
    if (!userProfile) return;

    try {
      setIsLoading(true);

      // Load quotes for this client
      const quotesQuery = query(
        collection(db, 'quotations'),
        where('email', '==', userProfile.email)
      );
      const quotesSnap = await getDocs(quotesQuery);
      const quotesData = quotesSnap.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as QuotationRequest[];
      // Sort by timestamp in JavaScript instead of Firestore
      quotesData.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setQuotes(quotesData);

      // Load bills for this client - try both email and company name matching
      let billsData: BillData[] = [];

      try {
        // First try to get bills by email
        const billsByEmailQuery = query(
          collection(db, 'bills'),
          where('clientEmail', '==', userProfile.email)
        );
        const billsByEmailSnap = await getDocs(billsByEmailQuery);
        const billsByEmail = billsByEmailSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })) as BillData[];
        // Sort by invoiceDate in JavaScript instead of Firestore
        billsByEmail.sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime());

        // Also try to get bills by company name (for cases where email doesn't match)
        // We'll filter these client-side since Firestore rules don't allow querying by clientCompanyName
        const allBillsQuery = query(
          collection(db, 'bills')
        );
        const allBillsSnap = await getDocs(allBillsQuery);
        const allBills = allBillsSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })) as BillData[];
        // Sort by invoiceDate in JavaScript instead of Firestore
        allBills.sort((a, b) => new Date(b.invoiceDate || b.createdAt).getTime() - new Date(a.invoiceDate || a.createdAt).getTime());

        // Filter bills that belong to this client (by email, company name, or client name)
        // Use multiple matching strategies to handle email/name variations
        const relevantBills = allBills.filter(bill => {
          const userEmail = userProfile.email.toLowerCase();
          const userDisplayName = userProfile.displayName?.toLowerCase() || '';
          const userCompanyName = (userProfile as any).companyName?.toLowerCase() || '';
          const userEmailPrefix = userEmail.split('@')[0].toLowerCase();

          const billEmail = (bill.clientEmail || '').toLowerCase();
          const billCompanyName = (bill.clientCompanyName || '').toLowerCase();
          const billClientName = (bill.clientName || '').toLowerCase();

          // Helper function to check if two strings are similar (allowing for typos)
          const areSimilar = (str1: string, str2: string): boolean => {
            if (str1 === str2) return true;
            // Check if one contains the other
            if (str1.includes(str2) || str2.includes(str1)) return true;
            // Check for common typos (e.g., mangalam vs manganam)
            const words1 = str1.split(/[\s.-]+/);
            const words2 = str2.split(/[\s.-]+/);
            return words1.some(word1 => words2.some(word2 =>
              word1.length > 3 && word2.length > 3 &&
              (word1.includes(word2) || word2.includes(word1))
            ));
          };

          return (
            // Direct email match
            billEmail === userEmail ||
            // Company name match (for corporate logins)
            billCompanyName === userEmailPrefix ||
            billCompanyName === userDisplayName ||
            billCompanyName === userCompanyName ||
            areSimilar(billCompanyName, userCompanyName) ||
            areSimilar(billCompanyName, userEmailPrefix) ||
            // Client name match
            billClientName === userDisplayName ||
            billClientName === userEmailPrefix ||
            billClientName === userCompanyName ||
            areSimilar(billClientName, userCompanyName) ||
            // Partial matches for flexibility
            billEmail.includes(userEmailPrefix) ||
            billCompanyName.includes(userEmailPrefix) ||
            billClientName.includes(userEmailPrefix) ||
            // Check if company name matches any part of the email
            userEmail.includes(billCompanyName) ||
            // Check if display name matches company name variations
            (userCompanyName && billCompanyName.includes(userCompanyName.split(' ')[0]))
          );
        });

        // Combine and deduplicate
        const combinedBills = [...billsByEmail, ...relevantBills];
        const uniqueBills = combinedBills.filter((bill, index, self) =>
          index === self.findIndex(b => b.id === bill.id)
        );

        billsData = uniqueBills;
      } catch (error) {
        console.error('Error loading bills:', error);
        billsData = [];
      }

      setBills(billsData);

      // Load invoices for this client
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('email', '==', userProfile.email)
      );
      const invoicesSnap = await getDocs(invoicesQuery);
      const invoicesData = invoicesSnap.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Invoice[];
      // Sort by createdAt in JavaScript instead of Firestore
      invoicesData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setInvoices(invoicesData);

      // Load projects for this client (mock data for now)
      // In production, this would query projects collection
      setProjects([]);

    } catch (error) {
      console.error('Error loading client data:', error);
      showError('Loading Error', 'Failed to load your data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      showError('Logout Error', 'Failed to logout. Please try again.');
    }
  };

  const handleMakePayment = (bill: BillData) => {
    setSelectedBillForPayment(bill);
    setShowPaymentModal(true);
  };

  const handleDownloadQuotation = async (quote: QuotationRequest) => {
    try {
      if (!quote.invoiceNumber) {
        showError('Download Error', 'Quotation not yet approved or generated');
        return;
      }

      // Generate quotation PDF data
      const amount = quote.amount || 0;
      const quotationData = {
        quotationNumber: quote.invoiceNumber || `Q-${quote.id}`,
        date: new Date(quote.timestamp).toLocaleDateString(),
        clientName: quote.clientName,
        clientAddress: quote.clientAddress || 'Client Address',
        attention: quote.attention || 'Client',
        subject: quote.subject || 'Quotation for NDT Services',
        salutation: 'Dear Sir/Madam,',
        introductoryText: 'We are pleased to submit our quotation for the NDT services as per your requirements.',
        items: [{
          srNo: 1,
          description: quote.description,
          qty: 1,
          uom: 'Nos',
          rate: amount,
          totalAmount: amount
        }],
        subTotal: amount,
        gstRate: 18,
        gstAmount: amount * 0.18,
        grandTotal: amount * 1.18,
        amountInWords: 'Zero Rupees Only', // This should be calculated
        bankDetails: {
          accountNumber: '1234567890',
          ifscCode: 'ABCD0123456',
          bankName: 'Sample Bank'
        },
        termsAndConditions: [
          'Payment terms: 100% advance',
          'Delivery: Within 7 days of order confirmation',
          'Validity: 30 days from quotation date'
        ],
        closingStatement: 'We look forward to your favorable response and assure you of our best services at all times.',
        companyName: 'AVIS Engineering & Consultancy',
        registeredOffice: 'Your Registered Office Address',
        contactNumbers: ['+91-1234567890'],
        email: 'info@avisengineering.com',
        website: 'www.avisengineering.com'
      };

      const { pdf } = await import('@react-pdf/renderer');
      const QuotationPDF = (await import('../services/quotationPDFGenerator')).default;

      const pdfDoc = pdf(<QuotationPDF quotationData={quotationData} />);
      const blob = await pdfDoc.toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotation-${quote.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess('Download Successful', 'Quotation PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading quotation PDF:', error);
      showError('Download Failed', 'Failed to download quotation PDF. Please try again.');
    }
  };

  const handlePaymentSuccess = async (paymentId: string, paymentRecord?: Omit<PaymentRecord, 'id'>) => {
    try {
      if (paymentRecord) {
        // Record payment in database
        const { recordPayment } = await import('../services/firebaseBilling');
        const paymentId = await recordPayment(paymentRecord);
        console.log('Payment recorded with ID:', paymentId);

        // Update bill status to Paid if it exists
        if (paymentRecord.billId) {
          const { updateBill } = await import('../services/firebaseBilling');
          const bills = await import('../services/firebaseBilling').then(m => m.listAllBills());
          const billToUpdate = bills.find(b => b.id === paymentRecord.billId);

          if (billToUpdate) {
            await updateBill({
              ...billToUpdate,
              status: 'Paid'
            });
          }
        }
      }

      showSuccess('Payment Recorded', 'Your payment has been successfully recorded!');
      // Refresh data to update bill status
      loadClientData();
    } catch (error) {
      console.error('Error recording payment:', error);
      showError('Payment Recording Failed', 'Payment was processed but failed to record. Please contact support.');
    }
  };

  if (isLoading) {
    return <EnhancedLoadingSpinner fullScreen text="Loading your dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img
                src="/logo61.png"
                alt="AVIS Engineering"
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Client Portal</h1>
                <p className="text-sm text-gray-500">Welcome back, {userProfile?.displayName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{userProfile?.companyName}</span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'quotes', label: 'My Quotes', icon: '📋' },
              { id: 'bills', label: 'My Bills', icon: '🧾' },
              { id: 'projects', label: 'Projects', icon: '🏗️' },
              { id: 'messages', label: 'Messages', icon: '💬' },
              { id: 'profile', label: 'Profile', icon: '👤' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-avis-blue text-avis-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-avis-blue to-avis-dark rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-2">Welcome to AVIS Engineering</h2>
              <p className="text-blue-100 mb-6">
                Your trusted partner for professional Non-Destructive Testing services
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="text-2xl mb-2">📋</div>
                  <div className="text-2xl font-bold">{quotes.length}</div>
                  <div className="text-blue-100">Active Quotes</div>
                </div>
                  <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                    <div className="text-2xl mb-2">🧾</div>
                    <div className="text-2xl font-bold">{bills.length}</div>
                    <div className="text-blue-100">Bills</div>
                  </div>
                <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="text-2xl mb-2">🏗️</div>
                  <div className="text-2xl font-bold">{projects.length}</div>
                  <div className="text-blue-100">Active Projects</div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h3>
              {quotes.length > 0 ? (
                <div className="space-y-4">
                  {quotes.slice(0, 5).map((quote) => (
                    <div key={quote.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900">Quote Request</h4>
                        <p className="text-sm text-gray-600">{quote.serviceType}</p>
                        <p className="text-xs text-gray-500">{new Date(quote.timestamp).toLocaleDateString()}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        quote.status === 'Approved' ? 'bg-green-100 text-green-800' :
                        quote.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {quote.status}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
                  <p className="text-gray-600 mb-6">Your quote requests and project updates will appear here.</p>
                  <a
                    href="/quotation"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-avis-blue hover:bg-avis-dark transition-colors"
                  >
                    Request a Quote
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'quotes' && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">My Quotes</h3>
            {quotes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(quote.timestamp).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {quote.serviceType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            quote.status === 'Approved' ? 'bg-green-100 text-green-800' :
                            quote.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {quote.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {quote.status === 'Approved' && quote.invoiceNumber ? (
                            <button
                              onClick={() => handleDownloadQuotation(quote)}
                              className="text-avis-blue hover:text-avis-dark bg-avis-blue bg-opacity-10 px-3 py-1 rounded-lg transition-colors"
                            >
                              Download PDF
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Quotes Yet</h3>
                <p className="text-gray-600 mb-6">You haven't requested any quotes yet.</p>
                <a
                  href="/quotation"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-avis-blue hover:bg-avis-dark transition-colors"
                >
                  Request Your First Quote
                </a>
              </div>
            )}
          </div>
        )}

        {activeTab === 'bills' && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">My Bills & Invoices</h3>
            {(bills.length > 0 || invoices.length > 0) ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Render bills */}
                    {bills.map((bill) => (
                      <tr key={`bill-${bill.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {bill.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(bill.invoiceDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {bill.items.length > 0 ? bill.items[0].description : 'NDT Services'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(() => {
                            const rounded = Math.round(bill.totalAmount);
                            return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            bill.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            bill.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {bill.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {bill.status !== 'Paid' && (
                            <button
                              onClick={() => handleMakePayment(bill)}
                              className="text-avis-blue hover:text-avis-dark bg-avis-blue bg-opacity-10 px-3 py-1 rounded-lg transition-colors"
                            >
                              Pay Now
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    
                    {/* Render invoices */}
                    {invoices.map((invoice) => (
                      <tr key={`invoice-${invoice.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(invoice.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {invoice.description || invoice.serviceType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(() => {
                            const rounded = Math.round(invoice.amount);
                            return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'Approved' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {invoice.status !== 'Paid' && (
                            <button
                              onClick={() => {
                                // Create a bill object from invoice for payment
                                const billForPayment: BillData = {
                                  id: invoice.id,
                                  companyName: 'AVIS Engineering & Consultancy',
                                  companySubtitle: 'NDT Services & Engineering Solutions',
                                  companyAddress: '',
                                  companyPhone: '',
                                  companyEmail: '',
                                  companyPAN: '',
                                  companyGSTN: '',
                                  invoiceNumber: invoice.invoiceNumber,
                                  invoiceDate: invoice.createdAt,
                                  workOrderNumber: '',
                                  workOrderDate: '',
                                  clientId: invoice.quotationId,
                                  clientName: invoice.clientName,
                                  clientAddress: '',
                                  clientGSTN: '',
                                  clientState: '',
                                  clientStateCode: '',
                                  clientEmail: invoice.email,
                                  clientCompanyName: invoice.clientName,
                                  items: [{
                                    srNo: 1,
                                    description: invoice.description || invoice.serviceType,
                                    sac: '998314',
                                    qty: 1,
                                    rate: invoice.amount,
                                    totalAmount: invoice.amount
                                  }],
                                  gstRate: 0,
                                  igstAmount: 0,
                                  subTotal: invoice.amount,
                                  cgstAmount: 0,
                                  sgstAmount: 0,
                                  totalGstAmount: 0,
                                  totalAmount: invoice.amount,
                                  bankDetails: {
                                    bankName: '',
                                    branch: '',
                                    accountNumber: '',
                                    ifscCode: ''
                                  },
                                  termsAndConditions: [],
                                  status: invoice.status === 'Paid' ? 'Paid' : 'Sent',
                                  createdAt: invoice.createdAt,
                                  updatedAt: invoice.updatedAt
                                };
                                handleMakePayment(billForPayment);
                              }}
                              className="text-avis-blue hover:text-avis-dark bg-avis-blue bg-opacity-10 px-3 py-1 rounded-lg transition-colors"
                            >
                              Pay Now
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🧾</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Bills Yet</h3>
                <p className="text-gray-600 mb-6">Your bills will appear here once they are generated by our team.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">My Projects</h3>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏗️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Project Tracking</h3>
              <p className="text-gray-600 mb-6">Track your ongoing projects and their progress.</p>
              <p className="text-sm text-gray-500">Coming Soon</p>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Messages & Notifications</h3>
              <button
                onClick={() => setShowMessaging(true)}
                className="bg-avis-blue text-white px-4 py-2 rounded-lg hover:bg-avis-dark transition-colors"
              >
                Open Messages
              </button>
            </div>
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">💬</div>
              <p>Click "Open Messages" to view your messages and notifications</p>
              <p className="text-sm">You'll receive updates about your quotes, invoices, and project status here</p>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">My Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                    <dd className="text-sm text-gray-900">{userProfile?.displayName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-900">{userProfile?.email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm text-gray-900">{userProfile?.phone}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Company Information</h4>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                    <dd className="text-sm text-gray-900">{userProfile?.companyName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                    <dd className="text-sm text-gray-900 capitalize">{userProfile?.role}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Member Since</dt>
                    <dd className="text-sm text-gray-900">
                      {userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          bill={selectedBillForPayment}
          clientEmail={userProfile?.email}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Messaging Modal */}
        <ClientMessaging
          isOpen={showMessaging}
          onClose={() => setShowMessaging(false)}
        />
      </div>
    </div>
  );
};

export default ClientDashboard;
